import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  MultiSelect,
  NumberInput,
  Form,
  FormGroup,
  ToastNotification,
} from "@carbon/react";
import "./createFuelRequestModal.scss";
import { queryClient } from "../..";
import { useTranslation } from "react-i18next";
import { useCreateRequest } from "../../hooks/useCreateRequest";
import ReactGA from "react-ga4";

export default function CreateFuelRequestModal({
  openModal,
  setOpenModal,
  agent,
  farmListData,
}) {
  const { t } = useTranslation();
  const [selectedFarms, setSelectedFarms] = useState([]);
  const [fuelValue, setFuelValue] = useState(100);
  const [errorNotification, setErrorNotification] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [touchedFarms, setTouchedFarms] = useState(false);
  const [touchedFuel, setTouchedFuel] = useState(false);

  const mutationCreateFuelRequest = useCreateRequest();

  const selectAllOption = {
    id: "select-all",
    login: t("admin.agentsList.modalRequest.selectAllFarms"),
    isSelectAll: true,
  };

  const approvedFarms = (farmListData || []).filter(
    (farm) => farm.state !== "NEW",
  );

  const farmsWithSelectAll =
    approvedFarms.length > 0 ? [selectAllOption, ...approvedFarms] : [];

  const handlePreventE = (evt) => {
    if (evt.key === "e") evt.preventDefault();
  };

  const fuelError = useMemo(() => {
    const value = Number(fuelValue);
    if (
      !fuelValue ||
      value < 100 ||
      Number.isNaN(value) ||
      value > 1000000 ||
      value % 100 !== 0
    ) {
      return t("admin.agentsList.modalRequest.validation.invalidValue");
    }
    return "";
  }, [fuelValue, t]);

  const farmsError = useMemo(() => {
    if (selectedFarms.length === 0) {
      return t("admin.agentsList.modalRequest.validation.noValue");
    }
    return "";
  }, [selectedFarms, t]);

  const noAvailableFarms = approvedFarms.length === 0;
  const isValid = !fuelError && !farmsError && !noAvailableFarms;
  const isSubmitDisabled = submitting || !isValid;

  const handleCreateFuelRequest = () => {
    if (isSubmitDisabled) return;
    setSubmitting(true);

    const farmsWithoutSelectAll = selectedFarms.filter(
      (farm) => !farm.isSelectAll,
    );

    mutationCreateFuelRequest.mutate(
      {
        agentid: agent.id,
        farms: farmsWithoutSelectAll.map((farm) => farm.id),
        fuel: Number(fuelValue),
      },
      {
        onSuccess: () => {
          ReactGA.event("fuel_request_create_success", {
            screen: "Admin_Agents",
            farms_count: farmsWithoutSelectAll.length,
            fuel_amount: Number(fuelValue),
          });

          queryClient.invalidateQueries({
            queryKey: ["fuel-request-list"],
            exact: true,
            refetchType: "inactive",
          });
          setOpenModal(false);
          setSubmitting(false);
        },
        onError: () => {
          ReactGA.event("fuel_request_create_error", {
            screen: "Admin_Agents",
          });

          setErrorNotification(true);
          setSubmitting(false);
        },
      },
    );
  };

  const handleClose = () => {
    ReactGA.event("modal_close", {
      modal_name: "fuel_request_create",
      screen: "Admin_Agents",
    });
    setOpenModal(false);
    setSubmitting(false);
    setErrorNotification(null);
    setTouchedFarms(false);
    setTouchedFuel(false);
  };

  useEffect(() => {
    ReactGA.event("modal_open", {
      modal_name: "fuel_request_create",
      screen: "Admin_Agents",
    });
  }, []);

  return (
    <Modal
      open={!!openModal}
      onRequestClose={handleClose}
      modalLabel={t("admin.modalText.label")}
      modalHeading={t("admin.agentsList.modalRequest.title")}
      primaryButtonText={t("admin.agentsList.modalRequest.buttonText")}
      secondaryButtonText={t("admin.modalText.cancel")}
      onRequestSubmit={handleCreateFuelRequest}
      primaryButtonDisabled={isSubmitDisabled}
      onSecondarySubmit={handleClose}
      closeButtonLabel={t("admin.modalText.close")}
    >
      <Form>
        <FormGroup
          legendText={t("admin.agentsList.modalRequest.formLegendText", {
            agentName: agent?.publicName,
          })}
          className="request_fuel_formgroup"
        >
          <MultiSelect
            id="multi-select-1"
            label={t("admin.agentsList.modalRequest.labelFarms")}
            autoAlign
            titleText={t("admin.agentsList.modalRequest.labelFarms")}
            helperText={t("admin.agentsList.modalRequest.descriptionFarms")}
            required
            items={farmsWithSelectAll}
            itemToString={(item) => (item ? item.login : "")}
            initialSelectedItems={selectedFarms}
            onChange={(e) => {
              setSelectedFarms(e.selectedItems);
              setTouchedFarms(true);
            }}
            invalid={touchedFarms && !!farmsError}
            invalidText={touchedFarms ? farmsError : ""}
            translateWithId={(messageId) => {
              if (messageId === "close.menu")
                return t("main.toolbar.tooltips.closeMenu");
              if (messageId === "open.menu")
                return t("main.toolbar.tooltips.openMenu");
              if (messageId === "clear.all")
                return t("main.toolbar.tooltips.clearAll");
            }}
          />

          <NumberInput
            id="number-input-1"
            label={t("admin.agentsList.modalRequest.labelFuel")}
            placeholder={t("admin.agentsList.modalRequest.placeholderFuel")}
            helperText={t("admin.agentsList.modalRequest.descriptionFuel")}
            value={fuelValue}
            onChange={(e) => {
              setFuelValue(e.target.value);
              setTouchedFuel(true);
            }}
            min={100}
            max={1000000}
            onKeyDown={handlePreventE}
            required
            allowEmpty
            hideSteppers
            step={100}
            invalid={touchedFuel && !!fuelError}
            invalidText={touchedFuel ? fuelError : ""}
          />
        </FormGroup>
      </Form>

      {errorNotification && (
        <ToastNotification
          kind="error"
          title={t("admin.agentsList.modalRequest.errorNotification.title")}
          subtitle={t(
            "admin.agentsList.modalRequest.errorNotification.subtitle",
          )}
          onCloseButtonClick={() => setErrorNotification(null)}
          lowContrast
          className="modal-error-notification"
        />
      )}
    </Modal>
  );
}
